import React, { useState } from "react";
import { BasicModal } from "../Modals/BasicModal/BasicModal";
import BasicButton from "../Buttons/BasicButton";
import useIsPhone from "../../hooks/useIsPhone";
import {
  detectInstallPlatform,
  isSnoozed,
  isStandalone,
  readDismissedAt,
  snoozeInstallBanner,
  useInstallPrompt,
} from "../../utils/installPrompt";
import styles from "./styles.module.scss";

const ICON = `${process.env.PUBLIC_URL}/apple-touch-icon.png`;

const svgProps = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

// The glyphs the steps name, drawn like the browsers' own
const ShareGlyph = () => (
  <svg {...svgProps} className={styles.glyph}>
    <path d="M12 3v12M8 7l4-4 4 4" />
    <path d="M8 10H6.5A1.5 1.5 0 0 0 5 11.5v8A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5v-8a1.5 1.5 0 0 0-1.5-1.5H16" />
  </svg>
);

const MoreGlyph = () => (
  <svg {...svgProps} className={styles.glyph}>
    <circle cx="6" cy="12" r="1.2" fill="currentColor" />
    <circle cx="12" cy="12" r="1.2" fill="currentColor" />
    <circle cx="18" cy="12" r="1.2" fill="currentColor" />
  </svg>
);

const AddGlyph = () => (
  <svg {...svgProps} className={styles.glyph}>
    <rect x="4" y="4" width="16" height="16" rx="4" />
    <path d="M12 8.5v7M8.5 12h7" />
  </svg>
);

const MenuGlyph = () => (
  <svg {...svgProps} className={styles.glyph}>
    <circle cx="12" cy="6" r="1.2" fill="currentColor" />
    <circle cx="12" cy="12" r="1.2" fill="currentColor" />
    <circle cx="12" cy="18" r="1.2" fill="currentColor" />
  </svg>
);

const CloseGlyph = () => (
  <svg {...svgProps} width="20" height="20">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

type GuideProps = {
  platform: "ios" | "android";
  isOpened: boolean;
  onClose: () => void;
  onDone: () => void;
};

/** How to add the app by hand: iOS, or Android without Chrome's dialog. */
const InstallGuide = ({ platform, isOpened, onClose, onDone }: GuideProps) => {
  const inSafari =
    platform === "ios" && !/CriOS|FxiOS|EdgiOS/.test(navigator.userAgent);

  const steps =
    platform === "ios"
      ? [
          inSafari ? (
            <>
              Tap <ShareGlyph /> <b>Share</b> in the bottom bar. No such button?
              Tap <MoreGlyph /> first.
            </>
          ) : (
            <>
              Tap <ShareGlyph /> <b>Share</b> in the address bar or the browser
              menu.
            </>
          ),
          <>
            Scroll down and tap <AddGlyph /> <b>Add to Home Screen</b>.
          </>,
          <>
            Tap <b>Add</b>. App Installs appears on your Home Screen.
          </>,
        ]
      : [
          <>
            Tap <MenuGlyph /> to open the browser menu.
          </>,
          <>
            Tap <b>Install app</b> or <b>Add to Home screen</b>.
          </>,
          <>Confirm. App Installs appears on your Home screen.</>,
        ];

  return (
    <BasicModal
      isOpened={isOpened}
      closeModal={onClose}
      className={styles.guideModal}
    >
      <div className={styles.guide}>
        <img src={ICON} alt="" className={styles.guideIcon} />
        <h2 className={styles.guideTitle}>
          Add App Installs to your Home Screen
        </h2>
        <p className={styles.guideText}>
          It opens full screen, like an app, one tap from your Home Screen.
        </p>
        <ol className={styles.steps}>
          {steps.map((step, i) => (
            <li key={i} className={styles.step}>
              <span className={styles.stepNumber}>{i + 1}</span>
              <span className={styles.stepText}>{step}</span>
            </li>
          ))}
        </ol>
        <p className={styles.hint}>
          Opened from Telegram or another app? Open this page in{" "}
          {platform === "ios" ? "Safari" : "Chrome"} first: built-in browsers
          can't add to the Home Screen.
        </p>
        <BasicButton
          variant="primary"
          size="large"
          onClick={onDone}
          className={styles.doneButton}
        >
          Got it
        </BasicButton>
      </div>
    </BasicModal>
  );
};

/**
 * Phones only, in a browser tab: a strip above the top bar that offers to
 * put the app on the Home Screen. Install opens Chrome's dialog where there
 * is one, the steps otherwise; the close button hides the strip for a week.
 */
const InstallBanner = () => {
  const isPhone = useIsPhone();
  const { canPrompt, installed, prompt } = useInstallPrompt();
  const [hidden, setHidden] = useState(
    () => isStandalone() || isSnoozed(readDismissedAt(), Date.now()),
  );
  const [guideOpen, setGuideOpen] = useState(false);
  const [platform] = useState(() =>
    detectInstallPlatform(navigator.userAgent, navigator.maxTouchPoints),
  );

  // A narrow desktop window is not a phone to install on
  if (!isPhone || installed || platform === "other") return null;

  const snooze = () => {
    snoozeInstallBanner();
    setHidden(true);
    setGuideOpen(false);
  };

  const install = async () => {
    if (!canPrompt) {
      setGuideOpen(true);
      return;
    }
    const outcome = await prompt();
    if (outcome === "dismissed") snooze();
  };

  return (
    <>
      {!hidden && (
        <div className={styles.banner} role="region" aria-label="Install app">
          <img src={ICON} alt="" className={styles.icon} />
          <div className={styles.text}>
            <span className={styles.title}>App Installs</span>
            <span className={styles.subtitle}>
              Add the app to your Home Screen
            </span>
          </div>
          <BasicButton variant="primary" size="small" onClick={install}>
            Install
          </BasicButton>
          <button
            type="button"
            className={styles.close}
            onClick={snooze}
            aria-label="Hide for a week"
          >
            <CloseGlyph />
          </button>
        </div>
      )}
      <InstallGuide
        platform={platform}
        isOpened={guideOpen}
        onClose={() => setGuideOpen(false)}
        onDone={snooze}
      />
    </>
  );
};

export default InstallBanner;
